// Format date helper
/* eslint-disable prettier/prettier */
/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Button,
    TextField,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    IconButton,
    Typography,
    TablePagination,
    Menu,
    ListItemIcon,
    ListItemText,
    MenuItem as MenuItemMui,
    Divider
} from '@mui/material';
import { FileDownload as FileDownloadIcon, Search as SearchIcon, Clear as ClearIcon, FilterAlt as FilterAltIcon, AllInclusive as AllInclusiveIcon, FilterList as FilterListIcon } from '@mui/icons-material';
import { StyledTableCell, StyledTableRow } from '../../ConstantColors'; // Assuming this is available
import MainCard from 'ui-component/cards/MainCard'; // Assuming this is available
import { useAcquittalsData } from 'hooks/api/travel-acquital/useAcquittalsTable'; // Import our custom hook

const formatDate = (dateString) => {
    try {
        return new Date(dateString).toLocaleDateString();
    } catch (error) {
        return 'Invalid Date';
    }
}; // Assuming date-fns is used for date formatting

const AcquittalsTable = () => {
    // Custom hook for acquittals data and operations
    const { acquittals, loading, fetchAcquittals, exportAcquittalsToCSV } = useAcquittalsData();

    // Get current month's start and end dates in YYYY-MM-DD format
    const getCurrentMonthDates = () => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        // Format dates to YYYY-MM-DD
        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        return {
            firstDay: formatDate(firstDay),
            lastDay: formatDate(lastDay)
        };
    };

    const { firstDay, lastDay } = getCurrentMonthDates();

    // State for filters
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [startDate, setStartDate] = useState(firstDay); // Default to first day of current month
    const [endDate, setEndDate] = useState(lastDay); // Default to last day of current month

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    // Export state
    const [exportAnchorEl, setExportAnchorEl] = useState(null);

    // Initialize with data fetch
    useEffect(() => {
        const fetchData = async () => {
            // Prepare API parameters based on current filters and pagination
            const params = {
                page,
                size: rowsPerPage,
                pageable: {
                    page,
                    size: rowsPerPage
                }
            };

            // Add filters if they're set
            if (statusFilter && statusFilter !== 'all') {
                params.acquittalStatus = statusFilter;
            }

            if (startDate) {
                params.startDate = startDate;
            }

            if (endDate) {
                params.endDate = endDate;
            }

            if (searchTerm) {
                params.search = searchTerm;
            }

            await fetchAcquittals(params);
        };

        fetchData();
    }, [fetchAcquittals, page, rowsPerPage, statusFilter, startDate, endDate, searchTerm]);

    // Get filtered data for export
    const getFilteredData = () => {
        if (!acquittals || !acquittals.content) {
            return [];
        }

        // Start with all data
        let filteredData = [...acquittals.content];

        // Apply filters in memory for export (client-side filtering)
        if (searchTerm) {
            const lowerSearchTerm = searchTerm.toLowerCase();
            filteredData = filteredData.filter((item) => {
                const travelerName = item.user ? `${item.user.firstName} ${item.user.lastName}`.toLowerCase() : '';
                const planName = item.travelPlan ? item.travelPlan.planName.toLowerCase() : '';
                return travelerName.includes(lowerSearchTerm) || planName.includes(lowerSearchTerm);
            });
        }

        if (statusFilter && statusFilter !== 'all') {
            filteredData = filteredData.filter((item) => {
                // Handle status determination based on latestAcquittalId
                const itemStatus = item.latestAcquittalId === null ? 'PENDING' : item.acquittalStatus || item.status;

                return itemStatus === statusFilter;
            });
        }

        if (startDate) {
            const startDateObj = new Date(startDate);
            filteredData = filteredData.filter((item) => {
                const itemDate = new Date(item.departureDatetime);
                return itemDate >= startDateObj;
            });
        }

        if (endDate) {
            const endDateObj = new Date(endDate);
            // Set to end of day
            endDateObj.setHours(23, 59, 59, 999);
            filteredData = filteredData.filter((item) => {
                const itemDate = new Date(item.departureDatetime);
                return itemDate <= endDateObj;
            });
        }

        return filteredData;
    };

    // Handle export menu
    const handleExportClick = (event) => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleExportClose = () => {
        setExportAnchorEl(null);
    };

    // Function to download current view (paginated) as CSV
    const downloadCurrentViewCSV = () => {
        const filteredData = getFilteredData();
        exportAcquittalsToCSV(filteredData);
        handleExportClose();
    };

    // Function to download all filtered data as CSV
    const downloadAllFilteredCSV = async () => {
        try {
            // Prepare API parameters with current filters but no pagination
            const params = {
                page: 0,
                size: 1000000, // Large number to get all records
                pageable: {
                    page: 0,
                    size: 1000000
                }
            };

            // Add current filters
            if (statusFilter && statusFilter !== 'all') {
                params.acquittalStatus = statusFilter;
            }
            if (startDate) {
                params.startDate = startDate;
            }
            if (endDate) {
                params.endDate = endDate;
            }
            if (searchTerm) {
                params.search = searchTerm;
            }

            // Fetch all filtered data
            await fetchAcquittals(params);
            if (acquittals && acquittals.content) {
                exportAcquittalsToCSV(acquittals.content);
            }
        } catch (error) {
            console.error('Error exporting filtered records:', error);
        }
        handleExportClose();
    };

    // Function to download complete dataset as CSV
    const downloadAllCSV = async () => {
        try {
            // Call the API with no filters to get all records
            const params = {
                page: 0,
                size: 1000000
            };
            await fetchAcquittals(params);
            if (acquittals && acquittals.content) {
                exportAcquittalsToCSV(acquittals.content);
            }
        } catch (error) {
            console.error('Error exporting all records:', error);
        }
        handleExportClose();
    };

    // Function to clear all filters
    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter('all');
        // Reset to current month instead of empty dates
        const { firstDay, lastDay } = getCurrentMonthDates();
        setStartDate(firstDay);
        setEndDate(lastDay);
        setPage(0); // Reset to first page when clearing filters
    };

    // Get data for the table
    const getTableData = () => {
        if (!acquittals || !acquittals.content) return [];
        return acquittals.content;
    };

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Render status chip
    const renderStatusChip = (status) => {
        let color = '#800020'; // Default for unknown
        let displayText = status;

        // Status color mapping
        switch (status) {
            case 'APPROVED':
                color = '#1C4B45'; // Dark green
                displayText = 'Approved';
                break;
            case 'PENDING':
                color = '#FF8C00'; // Dark orange
                displayText = 'Not Acquitted'; // Shortened to prevent cutoff
                break;
            default:
                color = '#800020'; // Default maroon
        }

        return (
            <Chip
                label={displayText}
                size="small"
                sx={{
                    backgroundColor: color,
                    color: 'white',
                    maxWidth: '100%' // Ensure chip respects column width
                }}
            />
        );
    };

    // Calculate acquittal due date (return date + 3 days)
    const calculateDueDate = (returnDate) => {
        if (!returnDate) return { display: 'N/A', isPast: false };

        try {
            const date = new Date(returnDate);
            date.setDate(date.getDate() + 3); // Add 3 days

            // Check if the due date has passed
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Normalize to start of day
            const isPast = date < today;

            return {
                display: formatDate(date),
                date,
                isPast
            };
        } catch (error) {
            return { display: 'Invalid Date', isPast: false };
        }
    };

    return (
        <MainCard
            title="Acquittals Tracking"
            secondary={
                <>
                    <Button 
                        variant="contained" 
                        startIcon={<FileDownloadIcon />} 
                        onClick={handleExportClick}
                        size="small" 
                        disabled={loading}
                    >
                        Export CSV
                    </Button>
                    <Menu
                        anchorEl={exportAnchorEl}
                        open={Boolean(exportAnchorEl)}
                        onClose={handleExportClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right'
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right'
                        }}
                    >
                        <MenuItemMui onClick={downloadCurrentViewCSV}>
                            <ListItemIcon>
                                <FilterAltIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText 
                                primary="Export Current View"
                                secondary="Export current page with applied filters"
                            />
                        </MenuItemMui>
                        <MenuItemMui onClick={downloadAllFilteredCSV}>
                            <ListItemIcon>
                                <FilterListIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText 
                                primary="Export All Filtered Records"
                                secondary="Export all records matching current filters"
                            />
                        </MenuItemMui>
                        <Divider />
                        <MenuItemMui onClick={downloadAllCSV}>
                            <ListItemIcon>
                                <AllInclusiveIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText 
                                primary="Export Complete Dataset"
                                secondary="Export all records without filters"
                            />
                        </MenuItemMui>
                    </Menu>
                </>
            }
        >
            {/* Filters */}
            <Box sx={{ mb: 3 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                    <TextField
                        label="Search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        InputProps={{
                            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                            endAdornment: searchTerm && (
                                <IconButton size="small" onClick={() => setSearchTerm('')}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            )
                        }}
                    />

                    <FormControl variant="outlined" size="small" fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                            <MenuItem value="all">All Status</MenuItem>
                            <MenuItem value="PENDING">Not Acquitted</MenuItem>
                            <MenuItem value="APPROVED">Approved</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                    <TextField
                        label="Start Date (Current Month)"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        fullWidth
                        helperText="First day of current month by default"
                    />

                    <TextField
                        label="End Date (Current Month)"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        fullWidth
                        helperText="Last day of current month by default"
                    />

                    <Button
                        variant="outlined"
                        onClick={clearFilters}
                        size="small"
                        sx={{ minWidth: '120px', alignSelf: 'flex-start', mt: 1 }}
                    >
                        Clear Filters
                    </Button>
                </Stack>
            </Box>

            {/* Loading indicator */}
            {loading && (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body1">Loading acquittal data...</Typography>
                </Box>
            )}

            {/* Table */}
            {!loading && acquittals && acquittals.content && (
                <Box sx={{ mb: 3 }}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 700 }} aria-label="acquittals table">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>Request ID</StyledTableCell>
                                    <StyledTableCell>Traveler Name</StyledTableCell>
                                    <StyledTableCell>Trip Name</StyledTableCell>
                                    <StyledTableCell align="center">Departure Date</StyledTableCell>
                                    <StyledTableCell align="center">Return Date</StyledTableCell>
                                    <StyledTableCell align="center">Acquittal Due Date</StyledTableCell>
                                    <StyledTableCell align="center">Budget Amount</StyledTableCell>
                                    <StyledTableCell align="center">Acquittal Status</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {getTableData().length > 0 ? (
                                    getTableData().map((row) => (
                                        <StyledTableRow key={row.id} hover>
                                            <StyledTableCell>{row.sequenceNumber || row.id}</StyledTableCell>
                                            <StyledTableCell component="th" scope="row">
                                                {row.user ? `${row.user.firstName} ${row.user.lastName}` : 'Unknown'}
                                            </StyledTableCell>
                                            <StyledTableCell>{row.travelPlan ? row.travelPlan.planName : 'Unknown'}</StyledTableCell>
                                            <StyledTableCell align="center">{formatDate(row.departureDatetime)}</StyledTableCell>
                                            <StyledTableCell align="center">{formatDate(row.returnDatetime)}</StyledTableCell>
                                            <StyledTableCell
                                                align="center"
                                                sx={{
                                                    color: (() => {
                                                        const dueInfo = calculateDueDate(row.returnDatetime);
                                                        return dueInfo.isPast && row.latestAcquittalId === null ? 'error.main' : 'inherit';
                                                    })(),
                                                    fontWeight: (() => {
                                                        const dueInfo = calculateDueDate(row.returnDatetime);
                                                        return dueInfo.isPast && row.latestAcquittalId === null ? 'bold' : 'normal';
                                                    })()
                                                }}
                                            >
                                                {(() => {
                                                    const dueInfo = calculateDueDate(row.returnDatetime);
                                                    if (dueInfo.isPast && row.latestAcquittalId === null) {
                                                        return (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                {dueInfo.display}
                                                                <Chip
                                                                    label="OVERDUE"
                                                                    size="small"
                                                                    color="error"
                                                                    sx={{ ml: 1, height: '20px', fontSize: '0.7rem' }}
                                                                />
                                                            </Box>
                                                        );
                                                    }
                                                    return dueInfo.display;
                                                })()}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                ${row.budgetAmount ? row.budgetAmount.toFixed(2) : '0.00'}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {renderStatusChip(
                                                    row.latestAcquittalId === null
                                                        ? 'PENDING'
                                                        : row.acquittalStatus || row.status || 'PENDING'
                                                )}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))
                                ) : (
                                    <StyledTableRow>
                                        <StyledTableCell colSpan={8} align="center">
                                            No acquittal data found
                                        </StyledTableCell>
                                    </StyledTableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    {acquittals && (
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={acquittals.totalElements || 0}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    )}
                </Box>
            )}

            {/* No data message */}
            {!loading && (!acquittals || !acquittals.content || acquittals.content.length === 0) && (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body1">No acquittal data found matching the filters.</Typography>
                </Box>
            )}
        </MainCard>
    );
};

export default AcquittalsTable;
